import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { formatCompactMoney, getProgramCardAccentColor } from './ProgramCard'
import {
  DashboardPeriod,
  DashboardProgramCard,
  DashboardProgramSpend,
} from '../dashboardTypes'

export type ServicesMetric = 'spend' | 'commuters'

const TYPE_ORDER = [
  'TRANSIT_PASS',
  'FLEX',
  'MICROMOBILITY',
  'GRH',
  'OTP',
  'COUPON',
  'REPORTED_USAGE',
]

const TYPE_LABEL: Record<string, string> = {
  TRANSIT_PASS: 'Transit passes',
  FLEX: 'Flex',
  MICROMOBILITY: 'Micromobility',
  GRH: 'Guaranteed ride home',
  OTP: 'One-time purchase',
  COUPON: 'Coupons',
  REPORTED_USAGE: 'Reported usage',
}

const SPEND_WINDOW: Record<DashboardPeriod, string> = {
  7: '7 day spend',
  30: '30 day spend',
  90: '90 day spend',
  all: 'all time spend',
}

const primaryType = (types: string[]) =>
  TYPE_ORDER.find((type) => types.includes(type)) ?? types[0] ?? 'OTHER'

export interface ProgramUsageRow {
  id: string
  name: string
  type: string
  typeLabel: string
  color: string
  spendCents: number
  enrolled: number
}

export interface ProgramTypeSegment {
  type: string
  label: string
  color: string
  value: number
  percent: number
}

const spendFor = (
  spendByPeriod: DashboardProgramSpend['spendByPeriod'] | undefined,
  period: DashboardPeriod
) => {
  if (!spendByPeriod) return 0
  const keyed = spendByPeriod as DashboardProgramSpend['spendByPeriod'] &
    Record<string, number>
  return keyed[period] ?? keyed[String(period)] ?? 0
}

export const topServiceRows = (
  services: DashboardProgramSpend[] | null | undefined,
  period: DashboardPeriod
): ProgramUsageRow[] | null => {
  if (!services) return null
  return services
    .map((service) => {
      const type = primaryType(service.types)
      return {
        id: service.launchId,
        name: service.name,
        type,
        typeLabel: TYPE_LABEL[type] ?? type,
        color: getProgramCardAccentColor(type === 'OTHER' ? [] : [type]),
        spendCents: spendFor(service.spendByPeriod, period),
        enrolled: service.enrolled ?? 0,
      }
    })
    .sort(
      (a, b) => b.spendCents - a.spendCents || a.name.localeCompare(b.name)
    )
}

export const programUsageRows = (
  cards: DashboardProgramCard[] | null | undefined,
  period: DashboardPeriod
): ProgramUsageRow[] | null => {
  if (!cards) return null
  return cards
    .map((card) => {
      const type = primaryType(card.types)
      return {
        id: card.launchId,
        name: card.superProgramName,
        type,
        typeLabel: TYPE_LABEL[type] ?? type,
        color: getProgramCardAccentColor(type === 'OTHER' ? [] : [type]),
        spendCents: card.byPeriod?.[period]?.moneySpentCents ?? 0,
        enrolled: card.enrolled ?? 0,
      }
    })
    .sort(
      (a, b) => b.spendCents - a.spendCents || a.name.localeCompare(b.name)
    )
}

export const programTypeMix = (
  rows: ProgramUsageRow[],
  metric: ServicesMetric
): ProgramTypeSegment[] => {
  const totals = new Map<string, ProgramTypeSegment>()
  rows.forEach((row) => {
    const value = metric === 'spend' ? row.spendCents : row.enrolled
    const current = totals.get(row.type)
    if (current) {
      current.value += value
      return
    }
    totals.set(row.type, {
      type: row.type,
      label: row.typeLabel,
      color: row.color,
      value,
      percent: 0,
    })
  })

  const segments = [
    ...TYPE_ORDER.filter((type) => totals.has(type)).map(
      (type) => totals.get(type) as ProgramTypeSegment
    ),
    ...[...totals.values()].filter(
      (segment) => !TYPE_ORDER.includes(segment.type)
    ),
  ]
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)
  if (total <= 0) return segments

  const withPercent = segments.map((segment) => ({
    ...segment,
    percent: Math.round((segment.value / total) * 100),
  }))
  const drift = 100 - withPercent.reduce((sum, segment) => sum + segment.percent, 0)
  if (drift !== 0) {
    const largest = withPercent.reduce((best, segment) =>
      segment.value > best.value ? segment : best
    )
    largest.percent += drift
  }
  return withPercent
}

const rowValue = (row: ProgramUsageRow, metric: ServicesMetric) =>
  metric === 'spend' ? row.spendCents : row.enrolled

const formatValue = (row: ProgramUsageRow, metric: ServicesMetric) =>
  metric === 'spend' ? formatCompactMoney(row.spendCents) : row.enrolled.toLocaleString('en-US')

const Toggle = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem;
  border-radius: 999px;
  background-color: ${palette.grey.grey200};
  flex-shrink: 0;
`

const ToggleOption = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.25;
  color: ${(props) =>
    props.$active ? palette.text.primary : palette.text.secondary};
  background-color: ${(props) => (props.$active ? palette.white : 'transparent')};
  box-shadow: ${(props) =>
    props.$active ? '0 1px 2px rgba(13, 12, 12, 0.08)' : 'none'};

  &:focus-visible {
    outline: 2px solid ${palette.primary.pink500};
    outline-offset: 2px;
  }
`

const SectionLabel = styled(Text)`
  display: block;
  margin-bottom: 0.5rem;
`

const MixTrack = styled.div`
  display: flex;
  width: 100%;
  height: 0.75rem;
  border-radius: 999px;
  overflow: hidden;
  background: ${palette.grey.grey200};
`

const MixSegment = styled.div<{ $color: string }>`
  height: 100%;
  background: ${(props) => props.$color};
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  margin: 0.75rem 0 1.25rem;
`

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`

const Dot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: ${(props) => props.$color};
  flex-shrink: 0;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 2fr) auto;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
`

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
`

const Track = styled.div`
  height: 0.5rem;
  border-radius: 999px;
  background: ${palette.grey.grey200};
  overflow: hidden;
`

const Fill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${(props) => props.$width}%;
  border-radius: 999px;
  background: ${(props) => props.$color};
`

const Amount = styled.div`
  min-width: 3.5rem;
  text-align: right;
`

interface ServicesMetricToggleProps {
  value: ServicesMetric
  onChange: (metric: ServicesMetric) => void
}

export const ServicesMetricToggle = ({
  value,
  onChange,
}: ServicesMetricToggleProps) => (
  <Toggle role='group' aria-label='Top services metric'>
    <ToggleOption
      type='button'
      $active={value === 'spend'}
      aria-pressed={value === 'spend'}
      onClick={() => onChange('spend')}
    >
      Spend
    </ToggleOption>
    <ToggleOption
      type='button'
      $active={value === 'commuters'}
      aria-pressed={value === 'commuters'}
      onClick={() => onChange('commuters')}
    >
      Commuters
    </ToggleOption>
  </Toggle>
)

interface ServicesUsedChartProps {
  programs: ProgramUsageRow[]
  metric: ServicesMetric
  period: DashboardPeriod
}

const ServicesUsedChart = ({
  programs,
  metric,
  period,
  ...props
}: ServicesUsedChartProps) => {
  if (!programs || programs.length === 0) return null

  const windowLabel = metric === 'spend' ? SPEND_WINDOW[period] : 'enrolled'
  const mix = programTypeMix(programs, metric)
  const total = mix.reduce((sum, segment) => sum + segment.value, 0)
  const ranked = [...programs].sort(
    (a, b) => rowValue(b, metric) - rowValue(a, metric) || a.name.localeCompare(b.name)
  )
  const maxValue = Math.max(...ranked.map((row) => rowValue(row, metric)), 0)

  return (
    <div {...props}>
      <SectionLabel variant='caption' color={palette.text.secondary}>
        Mode mix · {windowLabel}
      </SectionLabel>
      <MixTrack
        role='img'
        aria-label={`Mode mix, ${windowLabel}`}
        data-cy='services-mode-mix'
      >
        {total > 0
          ? mix
              .filter((segment) => segment.value > 0)
              .map((segment) => (
                <MixSegment
                  key={segment.type}
                  $color={segment.color}
                  style={{ width: `${(segment.value / total) * 100}%` }}
                />
              ))
          : null}
      </MixTrack>
      <Legend>
        {mix.map((segment) => (
          <LegendItem key={segment.type}>
            <Dot $color={segment.color} />
            <Text variant='body2' color={palette.text.secondary}>
              {segment.label}
            </Text>
            <Text variant='body2' fontWeight={600}>
              {segment.percent}%
            </Text>
          </LegendItem>
        ))}
      </Legend>

      <SectionLabel variant='caption' color={palette.text.secondary}>
        By program · {windowLabel}
      </SectionLabel>
      {ranked.map((program) => {
        const value = rowValue(program, metric)
        return (
          <Row key={program.id} data-cy='services-program-row'>
            <Name>
              <Dot $color={program.color} />
              <Text variant='body2' hideOverflow>
                {program.name}
              </Text>
            </Name>
            <Track>
              <Fill
                $color={program.color}
                $width={maxValue > 0 ? (value / maxValue) * 100 : 0}
              />
            </Track>
            <Amount>
              <Text variant='body2' fontWeight={600}>
                {formatValue(program, metric)}
              </Text>
            </Amount>
          </Row>
        )
      })}
    </div>
  )
}

export default ServicesUsedChart
